import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { MapPin, Calendar, Eye, LogOut, User } from 'lucide-react';

const PrestatairePortal = () => {
  const { user, logout } = useAuth();
  const [roadmaps, setRoadmaps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMyRoadmaps();
  }, []);

  const loadMyRoadmaps = async () => {
    try {
      const res = await axios.get(`${API}/roadmaps/my-roadmaps`, { withCredentials: true });
      setRoadmaps(res.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    window.location.href = '/';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-stone-100">
      {/* Header */}
      <header className="bg-white border-b border-stone-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_wedding-erp/artifacts/6y9ywpy7_image.png" 
              alt="Kolnoa" 
              className="h-12"
            />
            <div>
              <h1 className="text-xl font-bold text-stone-800">Portail Prestataire</h1>
              <p className="text-sm text-stone-600">Mes feuilles de route</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-stone-600">
              <User className="h-4 w-4" />
              <span className="text-sm">{user?.name}</span>
            </div>
            <Button variant="outline" onClick={handleLogout} className="flex items-center gap-2">
              <LogOut className="h-4 w-4" />
              Déconnexion
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-stone-800 mb-2">Mes missions</h2>
            <p className="text-stone-600">Feuilles de route où vous êtes assigné</p>
          </div>
          <Link to="/prestataire-calendar">
            <Button className="bg-amber-600 hover:bg-amber-700 flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Mon Calendrier
            </Button>
          </Link>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-stone-600">Chargement...</p>
          </div>
        ) : roadmaps.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <MapPin className="h-12 w-12 text-stone-400 mx-auto mb-4" />
              <p className="text-stone-600">Aucune feuille de route assignée pour le moment</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {roadmaps.map((roadmap) => (
              <Card key={roadmap.id} className="hover:shadow-lg transition-shadow">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">{roadmap.event_title}</CardTitle>
                  <div className="flex items-center gap-2 text-sm text-stone-600 mt-2">
                    <Calendar className="h-4 w-4" />
                    {roadmap.event_date}
                  </div>
                </CardHeader>
                <CardContent>
                  {roadmap.blocks && roadmap.blocks.length > 0 && (
                    <div className="space-y-2 mb-4">
                      {roadmap.blocks.slice(0, 2).map((block, i) => (
                        <div key={i} className="text-sm">
                          <div className="flex items-center gap-2 text-stone-800 font-medium">
                            <MapPin className="h-3 w-3 text-amber-600" />
                            {block.title}
                          </div>
                          <p className="text-xs text-stone-500 ml-5">{block.lieu}</p>
                        </div>
                      ))}
                      {roadmap.blocks.length > 2 && (
                        <p className="text-xs text-stone-500 ml-5">+{roadmap.blocks.length - 2} autres événements</p>
                      )}
                    </div>
                  )}
                  <Link to={`/roadmap/${roadmap.id}`} target="_blank">
                    <Button className="w-full bg-amber-600 hover:bg-amber-700">
                      <Eye className="h-4 w-4 mr-2" />
                      Voir la feuille de route
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default PrestatairePortal;
